<script lang="ts" setup>
import __MACROS_useVModel from "/vue-macros/define-models/use-vmodel";
import { ref, computed, onBeforeUnmount, onMounted, watch } from 'vue';
import { MIN_WIDTH, MAX_WIDTH, TableType } from '.';
import { FormBuilderContract } from '@/components/builder/form';
import { RootEntry } from '@/components/builder/form/entries/RootEntry';
import { NumericContract, Definition as NumericDefinition } from '../numeric';
import { evaluate } from 'mathjs';
import TableCellDisplay from './TableCellDisplay.vue';

interface SelectionState {
    isSelectedMode: boolean;
    startRowIndex: number | null;
    endRowIndex: number | null;
    colIndex: number | null;
}

const emit = defineEmits(["update:value", "update:idsRows", "copy-row", "copy-below", "copy-below-multi", "remove-row"]);

const { value, idsRows } = __MACROS_useVModel("value", "idsRows");

const props = defineProps({
  "value": null,
  "idsRows": null,
  "blueprint": null,
  "entry": null,
  "form": null,
  "design": { type: Boolean,  },
  "canAddRow": { type: Boolean,  },
  "canDeleteRow": { type: Function,  },
  "optimizeRowRendering": { type: Boolean,  },
  "requestedActiveRowIndex": null,
  "requestedActiveRowVersion": null,
  "reloadKey": null
});

const blueprint = computed(() => props.blueprint);
const columns = computed(() => blueprint.value.columns);
const footer = computed(() => blueprint.value.columns.some((item) => item.summary));
const columnsVisibility = computed(() => blueprint.value.components.map((_, i: number) => isColumnVisible(i)));
const summaryColumns = computed(() => blueprint.value.components
    .map((col, index) => ({ col, index }))
    .filter(({ index }) => blueprint.value.columns[index].summary));
const summaryByColumn = computed<Record<string, string>>(() =>
{
    if (props.design) return {};

    const result: Record<string, string> = {};

    summaryColumns.value.forEach(({ col }) =>
    {
        const columnBlueprint = props.form.schema.find(col.name) as NumericContract;
        const precision = columnBlueprint?.precision || 0;
        let sum = 0;

        value.value.forEach((item: any, rowIndex: number) =>
        {
            const data = item[col.name]?.data;
            const readonly = props.form.expressions.readonly(columnBlueprint, false, rowIndex);
            const resolved = data == null || (readonly && columnBlueprint?.defaultValue)
                ? props.form.expressions.executeExpression(columnBlueprint?.defaultValue, rowIndex)
                : data;

            if (item[col.name]?.type === NumericDefinition.type)
            {
                sum += Number(resolved || 0);
            }
        });

        result[col.name] = sum.toFixed(precision);
    });

    return result;
});


const showColumnOrdinalNumber = computed(() => blueprint.value.showColumnOrdinalNumber);
const tableRef = ref<HTMLElement | null>(null);
const activeRowIndex = ref<number | null>(null);
const activateRowTimeout = ref<number | null>(null);
const isFirefox = typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent);
const fullBlueprintTypes = [
    'dynamic-modal',
    'user',
    'user-group'
];

const selection = ref<SelectionState>({
    isSelectedMode: false,
    startRowIndex: null,
    endRowIndex: null,
    colIndex: null
});

const formulas = computed(() =>
{
    return blueprint.value.columns
        .map((item, index) => ({ formula: item.formula, field: blueprint.value.components[index].name }))
        .filter((item) => item.formula && item.field && item.field.toLowerCase().includes(NumericDefinition.type));
});

const columnsHeader = (header: Record<string, string>) =>
{
    return props.form.localization.translate(header);
};

const getScope = (index: number, field: string) =>
{
    const mappedScope: Record<string, number> = {};

    for (const key in value.value[index])
    {
        if (key.toLowerCase().includes(NumericDefinition.type) && key != field)
        {
            mappedScope[key] = value.value[index][key].data ?? 0;
        }
    }

    return mappedScope;
};

const removeRow = (index: number) =>
{
    value.value.splice(index, 1);
    idsRows.value.splice(index, 1);

    if (activeRowIndex.value === index) activeRowIndex.value = null;

    if (activeRowIndex.value != null && activeRowIndex.value > index) activeRowIndex.value -= 1;

    emit('remove-row', index);
};

const getWidth = (actualColumnIndex: number) =>
{
    const width = blueprint.value.columns[actualColumnIndex].width;

    function clamp(val: number, min: number, max: number)
    {
        return val > max ? max : val < min ? min : val;
    }

    return {
        ...(width ? { width: `${clamp(width, MIN_WIDTH, MAX_WIDTH)}px` } : {}),
    };
};

const getColumnPrecision = (col: any): number =>
{
    return col?.precision || 0;
};

const getAffixPrepend = (col: any): string =>
{
    return col?.affix?.prepend || '';
};

const getAffixAppend = (col: any): string =>
{
    return col?.affix?.append || '';
};

const resultFormula = (index: number) =>
{
    const formulasLength = formulas.value.length;

    for (let i = 0; i < formulasLength; i++)
    {
        const formula = formulas.value[i];
        const scope = getScope(index, formula.field);

        if (Object.values(scope)?.length > 0)
        {
            const result = evaluate(formula.formula, scope);

            value.value[index][formula.field].data = result;
        }
    }
};

const isColumnVisible = (index: number): boolean =>
{
    const formula = blueprint.value.columns[index].visibilityFormula;

    if (!formula) return true;

    if (props.design) return true;

    return props.form.expressions.executeExpression(formula);
};

const startFill = (startIndex: number, colIndex: number): void =>
{
    selection.value = {
        isSelectedMode: true,
        startRowIndex: startIndex,
        endRowIndex: startIndex,
        colIndex
    };

    document.body.style.userSelect = 'none';
    document.addEventListener('mouseup', endFill);
};

const hoverFill = (index: number): void =>
{
    if (selection.value.isSelectedMode)
    {
        selection.value.endRowIndex = index;
    }
};

const endFill = (): void =>
{
    if (selection.value.isSelectedMode)
    {
        const { startRowIndex, endRowIndex, colIndex } = selection.value;

        if (startRowIndex !== null && endRowIndex !== null && colIndex !== null)
        {
            copyBelowMulti(startRowIndex, endRowIndex, colIndex);
        }

        selection.value = {
            isSelectedMode: false,
            startRowIndex: null,
            endRowIndex: null,
            colIndex: null
        };

        document.body.style.userSelect = '';
        document.removeEventListener('mouseup', endFill);
    }
};

const copyBelow = (index: number, colIndex: number) =>
{
    emit('copy-below', index, colIndex);
};

const copyBelowMulti = (startRowIndex: number, endRowIndex: number, colIndex: number) =>
{
    emit('copy-below-multi', startRowIndex, endRowIndex, colIndex);
};

const copyRow = (index: number) =>
{
    emit('copy-row', index);
};

const isReadonly = (col: any, index: number) =>
{
    return props.form.expressions.readonly(col, false, index);
};

const activateRow = (index: number) =>
{
    if (!props.design && props.optimizeRowRendering)
    {
        if (!isFirefox)
        {
            activeRowIndex.value = index;

            return;
        }

        if (activateRowTimeout.value != null)
        {
            window.clearTimeout(activateRowTimeout.value);
        }

        // Firefox-specific: delay row activation so blur/change handlers
        // on currently focused inputs can commit value first.
        activateRowTimeout.value = window.setTimeout(() =>
        {
            activeRowIndex.value = index;
            activateRowTimeout.value = null;
        }, 0);
    }
};

const shouldAlwaysUseFullBlueprint = (col: any): boolean =>
{
    return fullBlueprintTypes.includes(col?.type);
};

const shouldShowEditor = (rowIndex: number, col: any): boolean =>
{
    if (shouldAlwaysUseFullBlueprint(col)) return true;

    if (!props.optimizeRowRendering) return true;

    if (props.design) return true;

    return activeRowIndex.value === rowIndex;
};

const handleOutsideClick = (event: MouseEvent) =>
{
    if (props.design || !props.optimizeRowRendering || !tableRef.value) return;

    const target = event.target as Node | null;

    if (target && !tableRef.value.contains(target))
    {
        activeRowIndex.value = null;
    }
};

onMounted(() =>
{
    document.addEventListener('click', handleOutsideClick);
});

watch(
    () => [props.requestedActiveRowVersion, props.requestedActiveRowIndex],
    () =>
    {
        if (!props.optimizeRowRendering) return;

        const index = props.requestedActiveRowIndex;

        if (index == null) return;

        if (index >= 0 && index < idsRows.value.length)
        {
            activeRowIndex.value = index;
        }
    }
);

onBeforeUnmount(() =>
{
    if (activateRowTimeout.value != null)
    {
        window.clearTimeout(activateRowTimeout.value);
    }

    document.removeEventListener('click', handleOutsideClick);
});
</script>

<template>
    <div ref="tableRef" class="scroll scroll-x pb-1">
        <table v-if="columns.length" class="table table-bordered mb-0">
            <colgroup>
                <col v-if="showColumnOrdinalNumber" style="width: 40px" />
                <col v-for="(col, index) in blueprint.components" v-show="columnsVisibility[index]" :key="col.id" :style="getWidth(index)" />
                <col v-if="idsRows.length && canAddRow" style="width: 108px" />
            </colgroup>
            <thead>
                <tr>
                    <th v-if="showColumnOrdinalNumber">{{ $t('[[[Lp.]]]') }}</th>
                    <th v-for="(col, index) in blueprint.components" v-show="columnsVisibility[index]" :key="col.id">
                        {{ columnsHeader(blueprint.columns[index].columnHeader) }}
                    </th>
                    <th v-if="idsRows.length && canAddRow"></th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(row, index) in idsRows" :key="row" @mousedown="activateRow(index)" @click="activateRow(index)">
                    <td v-if="showColumnOrdinalNumber" class="text-end align-middle">{{ index + 1 }}</td>
                    <td
                        v-for="(col, colIndex) in blueprint.components"
                        v-show="columnsVisibility[colIndex]"
                        :key="col.id"
                        class="position-relative table-cell"
                        :class="!design ? 'p-2 pb-0' : 'p-0'"
                        @mouseup="endFill"
                        @mouseover="hoverFill(index)"
                    >
                        <component
                            v-if="shouldShowEditor(index, col)"
                            :is="form.schema.designer(col.type)"
                            :form="form"
                            :parent="blueprint"
                            :blueprint="col"
                            :entry="entry"
                            :index="index"
                            width-full
                            disabled-drag
                            disabled-toolbar
                            :key="`${reloadKey}-${index}-${col.id}`"
                            @update="resultFormula(index)"
                        />
                        <table-cell-display
                            v-else
                            :form="form"
                            :blueprint="col"
                            :entry="value[index]?.[col.name]"
                            :row-index="index"
                        />
                        <div
                            v-if="shouldShowEditor(index, col) && !isReadonly(col, index)"
                            class="fill-handle"
                            @dblclick="copyBelow(index, colIndex)"
                            @mousedown="startFill(index, colIndex)"
                        />
                    </td>
                    <td v-if="canAddRow" class="align-middle">
                        <div class="d-flex justify-content-center">
                            <ideo-button
                                variant="warning"
                                icon="fas fa-clone"
                                :disabled="design"
                                @mousedown.stop
                                @click.stop="copyRow(index)"
                            />
                            <ideo-button
                                v-if="canDeleteRow(index)"
                                variant="danger"
                                icon="fas fa-trash"
                                :disabled="design"
                                @mousedown.stop
                                @click.stop="removeRow(index)"
                            />
                        </div>
                    </td>
                </tr>
                <tr v-if="idsRows.length === 0">
                    <td :colspan="blueprint.components.length" class="text-center">
                        <b>{{ $t('[[[Brak wyników]]]') }}</b>
                    </td>
                </tr>
            </tbody>
            <tfoot v-if="footer">
                <tr class="border-0">
                    <td v-if="showColumnOrdinalNumber" class="border-0"></td>
                    <td
                        v-for="(col, index) in blueprint.components"
                        v-show="columnsVisibility[index]"
                        :key="col.id"
                        :class="!blueprint.columns[index].summary ? 'border-0' : 'table-border'"
                    >
                        <ideo-input-group
                            v-if="blueprint.columns[index].summary"
                            :prepend="getAffixPrepend(col)"
                            :append="getAffixAppend(col)"
                        >
                            <input
                                type="number"
                                class="form-control"
                                :value="summaryByColumn[col.name] ?? Number(0).toFixed(getColumnPrecision(col))"
                                disabled
                            />
                        </ideo-input-group>
                    </td>
                </tr>
            </tfoot>
        </table>
        <p v-else>{{ $t('[[[Brak dodanych kolumn]]]') }}</p>
    </div>
</template>

<style lang="scss" scoped>
.table {
    table-layout: fixed;
    overflow: scroll;

    thead {
        tr {
            height: 36px;

            th {
                background: var(--bs-tertiary-bg);
            }
        }
    }

    &-border {
        background: var(--bs-tertiary-bg);
        border: 1px solid var(--bs-table-border-color);
    }
}

.table-error {
    margin-top: -12px;
}

.table-cell{
    .fill-handle {
        position: absolute;
        bottom: 6px;
        right: -4px;
        width: 12px;
        height: 12px;
        cursor: ns-resize;
        opacity: 0;
        transition: opacity 0.2s ease;
        background-color: transparent;

    &::before {
        content: '⋮';
        font-size: 12px;
        color: #555;
        }
    }

    &:hover {
        .fill-handle {
            opacity: 1;
        }
    }
}
</style>
